import { useMemo } from 'react';

import { GetOfferVoucherQuery, LocationsGeoFilterInput, useGetOfferVoucherQuery } from '@ocx/graphql';
import { useMembership } from '@ocx/data-membership';
import { useOffersLocationsGeoFilter } from '@ocx/feat-local-offers';
import { isReadyToUseOffer as getIsReadyToUseOffer, OPTIMIZED_VOUCHERS_FILTER, useOffer } from '@ocx/data-offers';
import { OfferDetailsAction, OfferDetailsAvailableAtLocation, messages } from '@ocx/ui-offers';

import { getAvailableAtLocations } from './get-available-at-locations.utils';
import { offerMessages } from './offer.messages';
import { useGetOfferLoyaltyAction } from './use-get-offer-loyalty-action.hook';
import { useReturnReward } from './use-return-reward.hook';

const getPointsSpendOfferIdToReturn = (response?: GetOfferVoucherQuery) => {
  const voucher = response?.customer?.membership?.offers?.edges?.[0]?.node.vouchers.edges?.[0]?.node || null;
  if (voucher?.source?.__typename !== 'OfferPurchase') {
    return null;
  }
  return voucher.source.purchasedWith.id;
};

type MemberCoordinates = { lat: number; lng: number } | null;

export type UseOfferDetailsControllerParams = {
  offerId: string;
  onOfferDetailsClose(): void;
  isLocalOffersEnabled: boolean;
  /** Member location for sorting “available at” list; omit when unknown. */
  memberCoordinates?: MemberCoordinates;
};

export const useOfferDetailsController = ({
  offerId,
  onOfferDetailsClose,
  isLocalOffersEnabled = false,
  memberCoordinates,
}: UseOfferDetailsControllerParams) => {
  const normalizedMemberCoordinates = memberCoordinates ?? null;
  const {
    membership: { isCompleted: membershipIsCompleted, points: membershipPoints },
  } = useMembership();

  const locationsGeoFilter: LocationsGeoFilterInput | undefined = useOffersLocationsGeoFilter({
    includeCoordinatesWhenShowingAll: true,
  });

  const { offer, loading: isOfferLoading } = useOffer({
    offerId,
    locationsGeoFilter: isLocalOffersEnabled ? locationsGeoFilter : undefined,
  });

  const isReadyToUseOffer = useMemo(() => (offer ? getIsReadyToUseOffer(offer) : false), [offer]);
  const { data: offerVoucherResponse, loading: isOfferVoucherLoading } = useGetOfferVoucherQuery({
    skip: !isReadyToUseOffer || !offer?.id,
    variables: {
      offerId: offer?.id || '',
      voucherFilter: OPTIMIZED_VOUCHERS_FILTER,
    },
    fetchPolicy: 'cache-and-network',
  });
  const { returnReward, returningReward } = useReturnReward({
    onCompleted: onOfferDetailsClose,
    preventRefetchOffer: { enabled: true, offerId },
  });
  const getOfferLoyaltyAction = useGetOfferLoyaltyAction({
    membershipIsCompleted,
    membershipPoints,
    onCompleted: onOfferDetailsClose,
  });

  const action = useMemo<OfferDetailsAction | null>(() => {
    const loyaltyAction = offer ? getOfferLoyaltyAction(offer) : ({ state: 'noAction' } as const);
    const pointSpendOfferId = getPointsSpendOfferIdToReturn(offerVoucherResponse);
    if (pointSpendOfferId) {
      return {
        title: messages.returnRewardButtonTitle,
        buttonVariant: 'outlined',
        onClick: () => {
          if (pointSpendOfferId) {
            returnReward(pointSpendOfferId);
          }
        },
        isExecuting: returningReward,
      };
    }
    if (loyaltyAction.state === 'hasAction') {
      return {
        onClick: () => loyaltyAction.onClick(),
        title: offerMessages[loyaltyAction.titleKey],
        isExecuting: loyaltyAction.isLoading,
      };
    }
    return null;
  }, [getOfferLoyaltyAction, offer, offerVoucherResponse, returnReward, returningReward]);

  const availableAtLocations: OfferDetailsAvailableAtLocation[] = useMemo(
    () => getAvailableAtLocations(offer, normalizedMemberCoordinates),
    [normalizedMemberCoordinates, offer],
  );

  return {
    offer,
    isOfferLoading,
    isOfferVoucherLoading,
    action,
    availableAtLocations,
    memberHasSharedLocation: !!normalizedMemberCoordinates,
  };
};
